import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, CardContent } from "../../ui/card";
import StatusBadge from "./StatusBadge";
import SkeletonCard from "./SkeletonCard";
import { User, ArrowLeft } from "lucide-react";

const AllReports = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Only allow admin access
  React.useEffect(() => {
    const role = localStorage.getItem('adminRole');
    if (role !== 'admin') {
      navigate('/admin');
    }
  }, [navigate]);
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' or 'month'
  const [page, setPage] = useState(1);
  const reportsPerPage = 10;
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, limit: reportsPerPage };
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/reports`,
          { params }
        );
        let reportsData = response.data.reports || response.data.data || [];
        let pagination = response.data.pagination || {};
        let total = pagination.total || reportsData.length;
        let pages = pagination.pages || 1;
        const enrichedReports = await Promise.all(
          reportsData.map(async (report) => {
            let songTitle = report.songTitle || t("recentlyEditedReports.noReports");
            if (!report.songTitle) {
              try {
                const songResponse = await axios.get(
                  `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${report.songID}`
                );
                songTitle = songResponse.data.title || t("recentlyEditedReports.noReports");
              } catch (songError) {
                // ignore
              }
            }
            return { ...report, songTitle };
          })
        );
        setReports(enrichedReports);
        setTotalReports(total);
        setTotalPages(pages);
      } catch (err) {
        setError(t("recentlyEditedReports.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [t, page]);

  // Filtering
  const now = new Date();
  const filteredReports = reports.filter((report) => {
    if (filter === 'month') {
      const created = new Date(report.createdAt);
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }
    return true;
  });
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const handleFilterChange = (val) => { setFilter(val); setPage(1); };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const formatLanguage = (language) => language.charAt(0).toUpperCase() + language.slice(1);
  const formatStatus = (status) => t(`recentlyEditedReports.status.${status.toLowerCase()}`);

  return (
    <div className="max-w-4xl mx-auto py-6 px-2">
      <button
        className="mb-4 flex items-center gap-2 text-orange-500 hover:text-orange-700 text-sm font-medium"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4" /> {t("recentlyEditedReports.back", "Back")}
      </button>
      <h1 className="text-2xl font-bold mb-4 text-neutral-800">{t("recentlyEditedReports.allReportsTitle", "All Reports")}</h1>
      {/* Report Count */}
      <div className="mb-2 text-gray-500 text-sm font-medium">
        {t('recentlyEditedReports.showingCount', { count: (page - 1) * reportsPerPage + reports.length, total: totalReports }) || `Showing ${(page - 1) * reportsPerPage + reports.length} of ${totalReports} reports`}
      </div>
      {/* Filter Bar */}
      <div className="flex gap-2 mb-6">
        <button
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${filter === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          onClick={() => handleFilterChange('all')}
        >
          {t('recentlyEditedReports.allTime', 'All Time')}
        </button>
        <button
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${filter === 'month' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          onClick={() => handleFilterChange('month')}
        >
          {t('recentlyEditedReports.thisMonth', 'This Month')}
        </button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-2xl border border-gray-100 shadow">
          <CardContent className="p-4 text-red-500 text-center">{error}</CardContent>
        </Card>
      ) : filteredReports.length === 0 ? (
        <Card className="rounded-2xl border border-gray-100 shadow">
          <CardContent className="py-8 text-center text-neutral-700 text-sm">
            {t("recentlyEditedReports.noReports")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredReports.map((report) => (
              <Card
                key={report._id}
                className="rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow group cursor-pointer"
                onClick={() => navigate(`/edit-song/${report.songID}`)}
              >
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="bg-zinc-100 rounded-full p-2">
                      <User className="w-6 h-6 text-zinc-400" />
                    </div>
                    <div>
                      <div className="font-medium text-neutral-800 text-base truncate">
                        {report.songTitle}
                      </div>
                      <div className="text-xs text-zinc-400">
                        {formatLanguage(report.language)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={report.status}>
                      {formatStatus(report.status)}
                    </StatusBadge>
                    <span className="text-xs text-zinc-400 ml-2">
                      {formatDate(report.createdAt)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Pagination Controls */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
              onClick={handlePrev}
              disabled={page === 1}
            >
              {t('recentlyEditedReports.prev', 'Prev')}
            </button>
            <span className="text-base text-gray-700 font-semibold">
              {page} / {totalPages}
            </span>
            <button
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
              onClick={handleNext}
              disabled={page === totalPages || totalPages === 0}
            >
              {t('recentlyEditedReports.next', 'Next')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AllReports; 